import { v4 as uuid } from 'uuid';
import { IFluidObject } from '@fluidframework/core-interfaces';
import {
  TokenProvider,
  ExternalContainer,
  LoadedInfo,
  ITelemetryNamespaceLogger,
  IframeLoadFailedError,
  LayoutMode,
  determineLayoutMode
} from '@fluidx/office-fluid-types';
import {
  ActivityTracker,
  Deferred,
  serializeError,
  safeLocalStorage,
  startDocumentHiddenTracker,
  sendTelemetryEvent,
  getLocalizationAttributes,
  getOrigin
} from '@fluidx/utilities';
import { DriverInfo, DriverType } from '../../DriverInfo';
import { ODSPNewFileInfo } from '../../DriverInfo/Odsp/resolveODSPDriverInfoForNewFile';
import { HostConfig } from '../../OfficeWebHostTypes';
import { FindIframe, FindIframeWindow } from '../Shared/FindIframe';
import {
  createPostMessageSender,
  PostMessageHandlers,
  PostMessageSender,
  PostMessage,
  setupPostMessageCollectionReceiver,
  PostMessageHandlersCollection,
  InitialComponentPath
} from '../Shared/PostMessage';
import {
  GetTokenMessage,
  IframedComponentParams,
  ReturnTokenMessage,
  LogMessage,
  ResizeMessage,
  RealizeReturnMessage,
  RealizeMessage,
  LoadComponentErrorMessage,
  NewFileInfoPromiseMessage,
  ToHostPostMessageId,
  messageVersion,
  MessageVersionErrorMessage,
  ReturnInitializeMessage,
  isCompatibleMessageVersion,
  minMessageVersion,
  Pairing,
  DisposeMessage
} from '../MessageTypes';
import { Activity, OWHEventSubCategories, OWHGenericEvent } from '../../Telemetry';
import { getScopeProxyInfo } from './getScopeProxyInfo';
import { getComponentInterfaceInfo } from './getComponentInterfaceInfo';
import { getContainerInterface } from './getContainerInterface';
import { handleIframeLoadFailure } from './handleIframeLoadFailure';
import {
  setParentOriginQueryParameter,
  setIframeModeQueryParameter,
  setSkipServiceWorkerQueryParameter
} from '../Shared/QueryParams';
import { IframeLoadOptions, DefaultIframeLoadOptions } from '../../loadComponentInIframe';
import { attemptTokenPrefetch } from '../../Iframe-shared/attemptTokenPrefetch';
import { LoadInIframeResult } from '../../Iframe-shared/LoadInIframeResult';
import { setupInitialIframeLayout } from '../../Iframe-shared/layout';
import { createBasicIframe } from '../../Iframe-shared/createBasicIframe';
import { logQoSError } from '../../Reliability/QoSErrorHelpers';
import { InitializeConfig } from '../../Iframe-shared/InitializeConfig';
import { mapProdOrDogfoodEnvironmentToIframeUrl } from './mapProdOrDogfoodEnvironmentToIframeUrl';
import { isDisposable } from '../Shared/Disposable';
import { OWHAshaErrorType, OWHAshaPillar, OWHAshaVeto } from '../../Reliability/OfficeWebHostAsha';

const LocalStorageKey_OfficeContainerIframeLoadTimeout = 'OfficeContainerIframeLoadTimeout';

enum LoadState {
  Failed = -1,
  FrameCreated = 1,
  FrameLoaded = 2,
  ComponentLoaded = 3
}

interface LoadContext {
  state?: LoadState;
  containerPromise?: Deferred<ExternalContainer>;
  componentPromise?: Deferred<IFluidObject>;
}

export function loadComponentInIframeInternal(
  initializeConfig: InitializeConfig,
  tokenProvider: TokenProvider,
  scope: IFluidObject,
  componentDiv: HTMLElement,
  config: HostConfig,
  logger: ITelemetryNamespaceLogger | undefined,
  bootstrapperStartTime: number,
  options: IframeLoadOptions
): LoadInIframeResult {
  // Used to track if the outside page was hidden at any point from here
  const documentHiddenTrackerId = startDocumentHiddenTracker();

  const loadComponentActivityTracker = ActivityTracker.start(Activity.LoadComponentInIframe, logger, {
    addUserTiming: true
  });

  // Note: "bootIframe" activity tracker represents only that an iframe was created and fired an "onload".
  // It does NOT represent the initialization call from within the iframe (which is captured
  // separately by an "iframeLoadAndInitialize" event that gets fired from within the iframe).
  const bootIframeActivityTracker = ActivityTracker.start(Activity.IFrameBoot, logger, {
    addUserTiming: true
  });

  const hostWindow = componentDiv.ownerDocument?.defaultView || window;

  // Remove parts of the host config which cannot be directly sent to the iframe
  const { driverImplementationOverride, ...configToProxy } = config;

  const layoutMode = config.componentRenderOptions?.layoutMode || determineLayoutMode(componentDiv);

  const iframeUrl = determineIframeUrl(options);
  const url = new URL(iframeUrl);
  setIframeModeQueryParameter(url, 'loadComponent');
  setParentOriginQueryParameter(url, location.origin);
  if (options.skipServiceWorker) {
    setSkipServiceWorkerQueryParameter(url);
  }

  const messagingId = uuid(); // Id for this iframe instance
  const iframeId = `fluid-iframe-${messagingId}`;

  // TODO: Task 4485007: Remove `|| ''` when iframeTitle is no longer optional.
  const iframe = createBasicIframe(options.iframeTitle || '');
  iframe.src = url.href;
  iframe.id = iframeId;

  // Make callbacks to find the iframe in the future so that we don't retain
  // references to them
  const { findIframe, findIframeWindow } = createFindIframeHelpers(componentDiv.ownerDocument, iframeId);

  let cleanupPostMessageReceiver: (() => void) | undefined;
  let cleanupPostMessageSender: (() => void) | undefined;
  let cleanupContextAndMessageHandlers: (() => void) | undefined;
  let iframeTimeoutHandle: number | undefined;
  const targetOrigin = getOrigin(iframeUrl);
  const sender = createPostMessageSender(hostWindow, findIframeWindow, targetOrigin);

  const loadedInfo = new Promise<LoadedInfo>((resolve, reject) => {
    const context: LoadContext = {
      state: LoadState.FrameCreated,
      componentPromise: new Deferred<IFluidObject>(),
      containerPromise: new Deferred<ExternalContainer>()
    };

    const iframeLoadStartTime = Date.now();
    componentDiv.appendChild(iframe);

    if (initializeConfig.type === 'Load') {
      attemptTokenPrefetch(tokenProvider, initializeConfig.driverInfo);
    }

    // The only way to know if we successfully loaded the iframe is to get back
    // an 'all clear' postMessage from it. There are 3 detectable failure modes:
    // 1) No page loads - based on a timeout (Likely means a networking issue)
    // 2) A page loads but no 'all clear' signal is received after trying to
    //    initialize its contents - based on a timeout (could be an invalid
    //    iframe url or a failure to detect an error case when attempting to
    //    load a component)
    // 3) The iframe signals that an error occurred while trying to load

    // Set up timeout for navigating the iframe
    const iframeTimeoutMs = determineIframeTimeoutMs(options.iframeLoadTimeout);
    if (iframeTimeoutMs > 0) {
      iframeTimeoutHandle = window.setTimeout(() => {
        context.state = LoadState.Failed;
        handleIframeLoadFailure({
          reject,
          bootIframeActivityTracker,
          loadComponentActivityTracker,
          documentHiddenTrackerId,
          logger
        });
      }, iframeTimeoutMs);
    }

    iframe.onload = async () => {
      if (context.state === LoadState.Failed) {
        return;
      }

      if (iframeTimeoutHandle !== undefined) {
        window.clearTimeout(iframeTimeoutHandle);
      }

      context.state = LoadState.FrameLoaded;
      bootIframeActivityTracker.setResult(true);

      cleanupPostMessageSender = () => sender.dispose();

      const handlersCollection: PostMessageHandlersCollection<any> = {};
      const { container, component, sendableScope, proxyPostMessageHandlers, dispose } = getProxiesAndHandlers(
        findIframe,
        scope,
        sender,
        handlersCollection
      );

      let disposeComponentsAndContainer: (() => void) | undefined = () => dispose();
      if (isDisposable(container)) {
        disposeComponentsAndContainer = createDisposeForComponentsAndContainer(dispose, container.dispose);
      }

      const postMessageHandlers: PostMessageHandlers<ToHostPostMessageId> = {
        ReturnInitialize: createReturnInitializeHandler(
          logger,
          sender,
          loadComponentActivityTracker,
          container,
          component,
          reject,
          context
        ),
        LoadComponentError: createLoadComponentErrorHandler(logger, loadComponentActivityTracker, context),
        MessageVersionError: createMessageVersionErrorHandler(logger, loadComponentActivityTracker, reject, context),
        GetToken: createGetTokenHandler(tokenProvider, sender, context),
        Log: maybeCreateLogHandler(logger)
      };

      setupInitialIframeLayout(layoutMode, iframe);
      const layoutHandlers = setupLayoutMessageHandlers(layoutMode, iframe, findIframe);

      handlersCollection[InitialComponentPath] = {
        ...handlersCollection[InitialComponentPath],
        ...proxyPostMessageHandlers,
        ...postMessageHandlers,
        ...layoutHandlers
      };

      const receiver = setupPostMessageCollectionReceiver(hostWindow, targetOrigin, handlersCollection, messagingId);

      cleanupPostMessageReceiver = () => receiver.remove();
      cleanupContextAndMessageHandlers = () => {
        // Cleanup components and container. There are several event listeners that get registered during initialization.
        // We must be sure to clean up ones that we know about in order to prevent any event listener leakage, or potential
        // situation where some object unintentionally holds onto references of other objects after disposal of the iframe.
        disposeComponentsAndContainer && disposeComponentsAndContainer();
        disposeComponentsAndContainer = undefined;

        if (handlersCollection[InitialComponentPath]) {
          for (const innerProp in handlersCollection[InitialComponentPath]) {
            (handlersCollection[InitialComponentPath] as any)[innerProp] = undefined;
          }
          handlersCollection[InitialComponentPath] = undefined;
        }

        for (const p in postMessageHandlers) {
          postMessageHandlers[p as ToHostPostMessageId] = undefined;
        }

        // Clean up context
        context.state = undefined;
        context.componentPromise = undefined;
        context.containerPromise = undefined;
      };

      const localization = getLocalizationAttributes();
      // Setting driver info to what old code expects it to look like.
      const driverInfo: DriverInfo =
        initializeConfig.type === 'Load'
          ? initializeConfig.driverInfo
          : {
              type: initializeConfig.driverType,
              documentUrl: '',
              newFileInfoPromise: Promise.resolve({}),
              componentId: '/'
            };

      const loadParams: IframedComponentParams = {
        containerEnvironment: config.environment,
        initializeConfig,
        // driverInfo deprecated on 10/06/2020, being kept in place for backwards compatibility, can be removed from here when *inside* iframe code has propagated
        driverInfo,
        identityType: tokenProvider.identityType || 'Enterprise', // Defaulting to 'Enterprise' for backward compatibility
        hostConfig: configToProxy,
        layoutMode,
        sendLogs: logger !== undefined,
        scope: sendableScope,
        bootstrapperStartTime,
        iframeLoadStartTime,
        messageVersion,
        messagingId,
        localization
      };

      sender.send({
        type: 'Initialize',
        payload: loadParams
      });

      let realize: (() => Promise<string>) | undefined = undefined;
      if (driverInfo.type === DriverType.SPO && driverInfo.newFileInfoPromise) {
        const newFileInfoPromise = driverInfo.newFileInfoPromise as Promise<ODSPNewFileInfo>;
        if (newFileInfoPromise) {
          newFileInfoPromise
            .then((value) => {
              const message: NewFileInfoPromiseMessage = { type: 'newFileInfoPromise', payload: value };
              sender.send(message);
            })
            .catch((e) => {
              const message: NewFileInfoPromiseMessage = {
                type: 'newFileInfoPromise',
                error: true,
                serializedError: serializeError(e)
              };
              sender.send(message);
            });
        }
        realize = () => {
          const realizeMessage: RealizeMessage = { type: 'Realize' };
          return sender
            .sendWithReturn<'Realize'>(realizeMessage)
            .then((reply) => (reply as RealizeReturnMessage).payload)
            .then((reply) => {
              if (!reply.error) {
                return reply.url;
              }
              throw reply.error;
            });
        };
      }

      // The context should already have a containerPromise, and componentPromise at this point.
      // They were initialized at the very beginning of load.
      resolve({
        container: context.containerPromise as Deferred<ExternalContainer>,
        component: context.componentPromise as Deferred<IFluidObject>,
        realize
      });
    };
  });

  return {
    loadedInfo,
    // TODO: Return a promise from dispose that resolves when we complete our cleanup tasks
    dispose: () => {
      // Clean up the iframe timeout handle when the iframe disposes
      if (iframeTimeoutHandle !== undefined) {
        window.clearTimeout(iframeTimeoutHandle);
      }

      const message: DisposeMessage = { type: 'Dispose' };
      sender.send(message);

      cleanupPostMessageReceiver && cleanupPostMessageReceiver();
      cleanupPostMessageReceiver = undefined;

      cleanupPostMessageSender && cleanupPostMessageSender();
      cleanupPostMessageSender = undefined;

      loadComponentActivityTracker.dispose();
      bootIframeActivityTracker.dispose();

      logger &&
        sendTelemetryEvent(logger, {
          eventName: OWHGenericEvent.IframeDisposed,
          subCategory: OWHEventSubCategories.IframeDisposed
        });

      cleanupContextAndMessageHandlers && cleanupContextAndMessageHandlers();
      cleanupContextAndMessageHandlers = undefined;
    }
  };
}

/**
 * Sets up the post message handlers to get the desired layout behavior.
 * @param layoutMode Determines the nature of the messages expected to be received
 * @param iframe Direct reference to the iframe. Should not be used in any message handlers.
 * @param getIframe How to find the iframe inside message handlers.
 * @returns Any post message handlers needed to update layout.
 */
function setupLayoutMessageHandlers(
  layoutMode: LayoutMode,
  iframe: HTMLIFrameElement,
  getIframe: FindIframe
): PostMessageHandlers<ToHostPostMessageId> {
  switch (layoutMode) {
    case LayoutMode.fixedByHost:
      iframe.style.height = '100%';
      iframe.style.width = '100%';
      return {};

    case LayoutMode.growHeight:
      iframe.style.width = '100%';
      return { ['Resize']: getResizeHandlerForGrowHeight(getIframe) };

    case LayoutMode.grow:
      return { ['Resize']: getResizeHandlerForGrow(getIframe) };

    default:
      throw new Error(`Invalid layout mode "${layoutMode}"`);
  }
}

function getResizeHandlerForGrowHeight(getIframe: FindIframe): (message: PostMessage<ToHostPostMessageId>) => void {
  return (message) => {
    const payload = (message as ResizeMessage).payload;
    if (message.payload) {
      const iframe = getIframe();
      if (iframe) {
        iframe.height = `${payload.height}`;
      }
    }
  };
}

function getResizeHandlerForGrow(getIframe: FindIframe): (message: PostMessage<ToHostPostMessageId>) => void {
  return (message) => {
    const payload = (message as ResizeMessage).payload;
    if (message.payload) {
      const iframe = getIframe();
      if (iframe) {
        iframe.height = `${payload.height}`;
        iframe.width = `${payload.width!}`;
      }
    }
  };
}

function getProxiesAndHandlers(
  getIframe: FindIframe,
  scope: IFluidObject,
  sender: PostMessageSender,
  handlersCollection: PostMessageHandlersCollection<string>
): {
  container: ExternalContainer;
  component: IFluidObject;
  sendableScope: Pairing<IFluidObject>;
  proxyPostMessageHandlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const { component, dispose: disposeComponents } = getComponentInterfaceInfo(
    getIframe,
    sender,
    InitialComponentPath,
    handlersCollection
  );
  const container = getContainerInterface(sender);

  const { sendableScope, scopeHandlerFactories, dispose: disposeProxies } = getScopeProxyInfo(sender, scope);
  const scopeHandlers: PostMessageHandlers<string> = scopeHandlerFactories.reduce((accumulator, factory) => {
    return { ...accumulator, ...factory(sender) };
  }, {});

  return {
    container,
    component,
    sendableScope,
    proxyPostMessageHandlers: {
      ...container.handlers,
      ...scopeHandlers
    },
    dispose: () => {
      disposeProxies();
      disposeComponents();
    }
  };
}

function determineIframeTimeoutMs(iframeLoadTimeout?: number): number {
  // A localStorage setting overrides value specified in parameter
  const storage = safeLocalStorage();
  if (storage !== undefined) {
    const possibleLocalStorageValue = storage[LocalStorageKey_OfficeContainerIframeLoadTimeout];
    if (possibleLocalStorageValue !== undefined) {
      // tslint:disable-next-line: radix
      const possibleLocalStorageValueNumeric: number = Number.parseInt(possibleLocalStorageValue);
      if (!Number.isNaN(possibleLocalStorageValueNumeric)) {
        return possibleLocalStorageValueNumeric;
      }
    }
  }

  if (iframeLoadTimeout !== undefined) {
    return iframeLoadTimeout;
  }

  return DefaultIframeLoadOptions.iframeLoadTimeout;
}

function determineIframeUrl(options: IframeLoadOptions): string {
  const { DoNotUse_iframeEnvironmentOverrideUrl: iframeEnvironmentOverrideUrl, iframeEnvironment } = options;

  if (iframeEnvironmentOverrideUrl) {
    return iframeEnvironmentOverrideUrl;
  }

  return mapProdOrDogfoodEnvironmentToIframeUrl(iframeEnvironment);
}

/**
 * Creates helper functions to retrieve the IFrame element and the IFrame's
 * window without directly referencing them (to help prevent memory leaks)
 * @param document the document which would contain the iframe
 * @param iframeId the id put on the IFrame element (so it can be found)
 */
function createFindIframeHelpers(
  document: Document | null,
  iframeId: string
): { findIframe: FindIframe; findIframeWindow: FindIframeWindow } {
  const findIframe = document ? () => document.querySelector<HTMLIFrameElement>(`#${iframeId}`) : () => null;
  const findIframeWindow = () => {
    const iframe = findIframe();
    return iframe ? iframe.contentWindow : null;
  };

  return { findIframe, findIframeWindow };
}

/**
 * Creates a post message handler for the 'log' message by relaying the log data to the given logger.
 */
function maybeCreateLogHandler(
  logger?: ITelemetryNamespaceLogger
): ((message: PostMessage<ToHostPostMessageId>) => void) | undefined {
  if (!logger) {
    return undefined;
  }

  return (message) => {
    if (message.payload) {
      const { namespace, event } = (message as LogMessage).payload;
      logger.sendWithNamespace(event, namespace);
    }
  };
}

function createGetTokenHandler(tokenProvider: TokenProvider, sender: PostMessageSender, context: LoadContext) {
  return async (message: PostMessage<ToHostPostMessageId>) => {
    if (context.state === LoadState.Failed) {
      return;
    }

    try {
      const payload = (message as GetTokenMessage).payload;

      const token = await tokenProvider.getToken(payload.scopes, payload.options);

      const response: ReturnTokenMessage = {
        payload: { token }
      };
      sender.sendReply(message, response);
    } catch (error) {
      const response: ReturnTokenMessage = {
        payload: { token: undefined }
      };
      sender.sendReply(message, response);
    }
  };
}

function createMessageVersionErrorHandler(
  logger: ITelemetryNamespaceLogger | undefined,
  loadComponentActivityTracker: ActivityTracker,
  reject: (reason?: any) => void,
  context: LoadContext
) {
  return (message: PostMessage<ToHostPostMessageId>) => {
    if (context.state === LoadState.Failed) {
      return;
    }

    context.state = LoadState.Failed;

    const payload = (message as MessageVersionErrorMessage).payload;
    const versions = {
      outsideVersion: messageVersion,
      insideVersion: payload.messageVersion,
      minVersion: payload.minMessageVersion
    };
    const error = new IframeLoadFailedError('Incompatible message version outside iframe', versions);
    loadComponentActivityTracker.setResult(false /*succeeded*/, { ...versions }, error);
    logger &&
      logQoSError(
        logger,
        Activity.LoadComponentInIframe,
        OWHAshaPillar.Open,
        OWHAshaVeto.FileOpenFailed,
        OWHAshaErrorType.IncompatibleMessageVersions,
        {} /*additionalProps*/,
        error
      );
    reject(error);
  };
}

function createLoadComponentErrorHandler(
  logger: ITelemetryNamespaceLogger | undefined,
  loadComponentActivityTracker: ActivityTracker,
  context: LoadContext
) {
  return (message: PostMessage<ToHostPostMessageId>) => {
    if (context.state === LoadState.Failed) {
      return;
    }

    context.state = LoadState.Failed;

    const errorPayload = (message as LoadComponentErrorMessage).payload;
    const error = errorPayload ? JSON.parse(errorPayload) : undefined;
    loadComponentActivityTracker.setResult(
      false /*succeeded*/,
      { errorType: error.errorType } /*additionalProps*/,
      error
    );
    logger &&
      logQoSError(
        logger,
        Activity.LoadComponentInIframe,
        OWHAshaPillar.Open,
        OWHAshaVeto.FileOpenFailed,
        error.errorType || OWHAshaErrorType.ComponentLoadError,
        {} /*additionalProps*/,
        error
      );

    // The context should already have a containerPromise, and componentPromise at this point.
    // They were initialized at the very beginning of load.
    context.containerPromise?.reject(error);
    context.componentPromise?.reject(error);
  };
}

function createReturnInitializeHandler(
  logger: ITelemetryNamespaceLogger | undefined,
  sender: PostMessageSender,
  loadComponentActivityTracker: ActivityTracker,
  container: ExternalContainer,
  component: IFluidObject,
  reject: (reason?: any) => void,
  context: LoadContext
) {
  return (message: PostMessage<ToHostPostMessageId>) => {
    if (context.state === LoadState.Failed) {
      return;
    }

    const givenMessageVersion = (message as ReturnInitializeMessage).payload?.messageVersion;
    if (!isCompatibleMessageVersion(givenMessageVersion)) {
      // Notify the inside of the iframe that the outside has rejected its version
      const errorMessage: MessageVersionErrorMessage = {
        type: 'MessageVersionError',
        payload: { messageVersion, minMessageVersion }
      };
      sender.send(errorMessage);

      // Reject the iframe initialization promise
      const versions = {
        outsideVersion: messageVersion,
        insideVersion: givenMessageVersion,
        minVersion: minMessageVersion
      };
      const error = new IframeLoadFailedError('Incompatible message version inside iframe', versions);
      loadComponentActivityTracker.setResult(false /*succeeded*/, { ...versions }, error);
      logger &&
        logQoSError(
          logger,
          Activity.LoadComponentInIframe,
          OWHAshaPillar.Open,
          OWHAshaVeto.FileOpenFailed,
          OWHAshaErrorType.IncompatibleMessageVersions,
          {} /*additionalProps*/,
          error
        );
      reject(error);

      return;
    }

    context.state = LoadState.ComponentLoaded;

    // The context should already have a containerPromise, and componentPromise at this point.
    // They were initialized at the very beginning of load.
    context.containerPromise?.resolve(container);
    context.componentPromise?.resolve(component);

    loadComponentActivityTracker.setResult(true);
  };
}

function createDisposeForComponentsAndContainer(disposeComponents: () => void, disposeContainer: () => void) {
  return () => {
    disposeComponents();
    disposeContainer();
  };
}

// cspell:ignore Iframed ODSP Asha Prefetch
